import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SettingsIcon from "@mui/icons-material/Settings";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { graphql } from "babel-plugin-relay/macro";
import { useFragment } from "react-relay";
import { InternBuildConfigPanel_appConfig$key } from "./__generated__/InternBuildConfigPanel_appConfig.graphql";

const appConfigFragment = graphql`
  fragment InternBuildConfigPanel_appConfig on BrandAppConfigType {
    appName
    # iOS Config
    iosAppIdentifier
    iosAscTeamId
    iosAscKeyId
    iosAscIssuerId
    appTrackingDialogEnabled
    # Android Config
    androidAppIdentifier
  }
`;

interface ConfigItemProps {
  label: string;
  value: string | boolean | null | undefined;
  masked?: boolean;
}

const ConfigItem = ({ label, value, masked = false }: ConfigItemProps) => {
  let displayValue: string;

  if (value === null || value === undefined || value === "") {
    displayValue = "Not configured";
  } else if (typeof value === "boolean") {
    displayValue = value ? "Yes" : "No";
  } else if (masked && value.length > 8) {
    displayValue = `${value.substring(0, 4)}...${value.substring(
      value.length - 4
    )}`;
  } else {
    displayValue = value;
  }

  const isConfigured =
    value !== null && value !== undefined && value !== "" && value !== false;

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        py: 0.75,
        px: 1,
        borderRadius: 0.5,
        "&:hover": {
          bgcolor: "var(--intern-bg-hover)",
        },
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: "var(--intern-text-tertiary)",
          fontSize: "0.6875rem",
          fontWeight: 500,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: isConfigured
            ? "var(--intern-text-primary)"
            : "var(--intern-text-tertiary)",
          fontSize: "0.75rem",
          fontWeight: isConfigured ? 500 : 400,
          fontFamily: isConfigured && !masked ? "monospace" : "inherit",
          fontStyle: isConfigured ? "normal" : "italic",
        }}
      >
        {displayValue}
      </Typography>
    </Box>
  );
};

interface Props {
  appConfig: InternBuildConfigPanel_appConfig$key;
}

const InternBuildConfigPanel = ({ appConfig: appConfigKey }: Props) => {
  const appConfig = useFragment(appConfigFragment, appConfigKey);

  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{
        bgcolor: "var(--intern-bg-primary)",
        border: "1px solid var(--intern-border-default)",
        borderRadius: "8px !important",
        overflow: "hidden",
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={
          <ExpandMoreIcon sx={{ color: "var(--intern-text-tertiary)" }} />
        }
        sx={{
          px: 2,
          minHeight: 52,
          "& .MuiAccordionSummary-content": { my: 1 },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <SettingsIcon
            sx={{ fontSize: 18, color: "var(--intern-text-tertiary)" }}
          />
          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                color: "var(--intern-text-primary)",
                fontWeight: 600,
                fontSize: "0.875rem",
              }}
            >
              Build Configuration
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "var(--intern-text-tertiary)",
                fontSize: "0.6875rem",
              }}
            >
              Bundle IDs, ASC credentials, package name
            </Typography>
          </Box>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
        <Stack spacing={2}>
          {/* iOS Configuration */}
          <Box>
            <Typography
              variant="overline"
              sx={{
                color: "#007AFF",
                fontSize: "0.625rem",
                fontWeight: 700,
                letterSpacing: "1px",
                mb: 1,
                display: "block",
              }}
            >
              iOS Configuration
            </Typography>
            <Box
              sx={{
                bgcolor: "var(--intern-bg-secondary)",
                borderRadius: 1,
                p: 1,
              }}
            >
              <ConfigItem
                label="Bundle ID"
                value={appConfig.iosAppIdentifier}
              />
              <ConfigItem label="Team ID" value={appConfig.iosAscTeamId} />
              <ConfigItem
                label="API Key ID"
                value={appConfig.iosAscKeyId}
                masked
              />
              <ConfigItem
                label="Issuer ID"
                value={appConfig.iosAscIssuerId}
                masked
              />
              <ConfigItem
                label="App Tracking Dialog"
                value={appConfig.appTrackingDialogEnabled}
              />
            </Box>
          </Box>

          <Divider sx={{ borderColor: "var(--intern-border-default)" }} />

          {/* Android Configuration */}
          <Box>
            <Typography
              variant="overline"
              sx={{
                color: "#3DDC84",
                fontSize: "0.625rem",
                fontWeight: 700,
                letterSpacing: "1px",
                mb: 1,
                display: "block",
              }}
            >
              Android Configuration
            </Typography>
            <Box
              sx={{
                bgcolor: "var(--intern-bg-secondary)",
                borderRadius: 1,
                p: 1,
              }}
            >
              <ConfigItem
                label="Package Name"
                value={appConfig.androidAppIdentifier}
              />
            </Box>
          </Box>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

export default InternBuildConfigPanel;
